import React, {useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {connect} from 'react-redux';
import {Layout, Tabs} from 'antd';

import {useT} from 'hooks/Locale/useT';
import generalHelpers from 'utils/generalHelpers';
import {VideoInstructionsCreateModal} from 'components/VideoInstructions';

import SupportToolbar from './SupportToolbar';
import InstructionsTab from './InstructionsTab';
import {useVideoInstructions} from './InstructionsTab/useVideoInstructions';
import SupportContactsTab from './SupportContactsTab';
import TermsTab from './TermsTab';
import ManualTab from './ManualTab';
import {SUPPORT_TAB_KEYS, SupportTabKey} from './types';

import './styles.scss';

const {Header} = Layout;

const mapStateToProps = (state: any) => {
    return {
        session: state.session
    }
};

interface SupportInterface {
    session: any;
}

const Support: React.FC<SupportInterface> = ({session}) => {
    const t = useT();
    const navigate = useNavigate();
    const params = useParams();

    const userRole = session.active_user_type;
    const canEdit: boolean = Boolean(session?.access?.ignore_access_permissions);
    const canViewInstructions: boolean =
        Array.isArray(session?.permissions) && generalHelpers.hasPermissions(session, 'view_instruction');
    const canSeeTerms = userRole !== 'SUPERVISION';

    const [showCreateModal, setShowCreateModal] = useState(false);
    const {instructions, instructionsLoading, positions, positionsLoading, reload} =
        useVideoInstructions(canViewInstructions);

    // Active tab is derived from the URL, never stored in state: an unknown or
    // missing :tab falls back to the first visible tab, and browser back/forward
    // work for free.
    const visibleKeys = SUPPORT_TAB_KEYS.filter(
        (key) =>
            (key !== 'instructions' || canViewInstructions) &&
            (key !== 'terms-and-conditions' || canSeeTerms)
    );
    const activeKey = visibleKeys.includes(params.tab as SupportTabKey)
        ? (params.tab as SupportTabKey)
        : visibleKeys[0];

    return (
        <Layout className='instructions-layout'>
            <Header className='instructions-header'>
                <div>{t('system.help')}</div>
            </Header>

            <div className='video-instructions-content'>
                <Tabs
                    activeKey={activeKey}
                    onChange={(key: string) => navigate(`/support/${key}`)}
                    tabBarExtraContent={
                        <SupportToolbar
                            activeKey={activeKey}
                            canEdit={canEdit}
                            userRole={userRole}
                            positions={positions}
                            positionsLoading={positionsLoading}
                            onPositionChange={reload}
                            onCreateClick={() => setShowCreateModal(true)}
                        />
                    }
                    items={[
                        ...(canViewInstructions ? [{
                            key: 'instructions',
                            label: t('system.video.instructions'),
                            children: <InstructionsTab instructions={instructions} loading={instructionsLoading} positions={positions} reload={reload} />,
                        }] : []),
                        {
                            key: 'help',
                            label: t('system.support'),
                            children: <SupportContactsTab />,
                        },
                        ...(canSeeTerms ? [{
                            key: 'terms-and-conditions',
                            label: t('system.terms_and_conditions'),
                            children: <TermsTab />,
                        }] : []),
                        {
                            key: 'student-manual',
                            label: t('system.student_manual'),
                            children: <ManualTab type='STUDENT' canEdit={canEdit} />,
                        },
                        {
                            key: 'supervision-manual',
                            label: t('system.supervision_manual'),
                            children: <ManualTab type='SUPERVISION' canEdit={canEdit} />,
                        },
                        {
                            key: 'lector-manual',
                            label: t('system.lector_manual'),
                            children: <ManualTab type='LECTOR' canEdit={canEdit} />,
                        },
                    ]}
                />
            </div>

            <VideoInstructionsCreateModal
                visible={showCreateModal}
                onCancel={() => setShowCreateModal(false)}
                afterSubmit={() => setShowCreateModal(false)}
                positions={positions}
                reloadInstructions={reload}
            />
        </Layout>
    );
};

export default connect(mapStateToProps)(Support);
